import { logError } from "utility/Function";
import {
  DEFAULT_LOCALE,
  SupportedLocale,
  getBrowserLanguageTag,
  resolveSupportedLocale,
} from "utility/locale";

/** Exported so `clearStorage` can preserve it — see the note there. */
export const LANGUAGE_CHOICE_KEY = "wone.i18n.languageChoice";

/**
 * A language the user picked in this browser.
 *
 * `pendingSync` is what makes the sign-in-screen picker work: it runs before
 * there is an account to write to, so the choice is held here and pushed to
 * the profile at the first authenticated moment. Once pushed it stops being
 * pending and `learner.language` leads again — which is what stops a stale
 * choice in one browser from clobbering a newer one made elsewhere.
 */
export interface StoredLanguageChoice {
  locale: SupportedLocale;
  pendingSync: boolean;
}

export function readLanguageChoice(): StoredLanguageChoice | null {
  try {
    const raw = window.localStorage.getItem(LANGUAGE_CHOICE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<StoredLanguageChoice>;
    // Re-resolve rather than trusting the stored string: a locale we no
    // longer support (or a corrupted value) must not reach changeLanguage.
    const locale = parsed.locale ? resolveSupportedLocale(parsed.locale) : null;
    if (!locale) return null;

    return { locale, pendingSync: parsed.pendingSync === true };
  } catch (error) {
    // Private browsing and blocked-storage modes throw on access.
    logError("Failed to read the stored language choice", error as Error);
    return null;
  }
}

export function writeLanguageChoice(choice: StoredLanguageChoice): void {
  try {
    window.localStorage.setItem(LANGUAGE_CHOICE_KEY, JSON.stringify(choice));
  } catch (error) {
    // Non-fatal: the app still switches language for this session, the
    // choice just isn't remembered across a reload.
    logError("Failed to persist the language choice", error as Error);
  }
}

/** Mark a stored choice as pushed to the profile. No-op if none is stored. */
export function markLanguageChoiceSynced(): void {
  const choice = readLanguageChoice();
  if (!choice || !choice.pendingSync) return;
  writeLanguageChoice({ ...choice, pendingSync: false });
}

/**
 * The locale to render in, given everything known at the time.
 *
 * Order, most to least authoritative:
 *
 * 1. a choice made in this browser that hasn't reached the profile yet — the
 *    user's most recent deliberate act, and the whole point of offering the
 *    picker before sign-in;
 * 2. the profile's language;
 * 3. any stored choice (already synced) — what the app renders before the
 *    profile loads;
 * 4. the browser locale, if we support it;
 * 5. `en-GB`.
 *
 * `languageIsExplicit` deliberately does not appear here. It governs whether
 * we may overwrite the profile (see `shouldSeedFromBrowser`), not what we
 * render: demoting a browser-seeded profile language below the current
 * browser locale would silently flip existing learners.
 */
export function resolveActiveLocale({
  stored,
  profileLanguage,
  browserTag = getBrowserLanguageTag(),
}: {
  stored: StoredLanguageChoice | null;
  profileLanguage?: string | null;
  browserTag?: string;
}): SupportedLocale {
  if (stored?.pendingSync) return stored.locale;

  if (profileLanguage) {
    const fromProfile = resolveSupportedLocale(profileLanguage);
    if (fromProfile) return fromProfile;
  }

  if (stored) return stored.locale;

  return resolveSupportedLocale(browserTag) ?? DEFAULT_LOCALE;
}

/**
 * Whether to seed the profile from the browser locale.
 *
 * Replaces `localeToSeed`'s `language == en-GB` proxy for "never chosen".
 * That proxy is what made a learner who deliberately picked English on a
 * French browser get re-seeded to French; `languageIsExplicit` is the signal
 * that tells the two apart. The `en-GB` check stays so an existing seed is
 * never quietly changed on a browser set to another language.
 */
export function shouldSeedFromBrowser(learner: {
  language: string;
  languageIsExplicit?: boolean;
}): boolean {
  return !learner.languageIsExplicit && learner.language === DEFAULT_LOCALE;
}
